import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Progress,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  OrderDocumentRetentionSnapshotSummary,
  OrderDocumentSummary,
  OrderDocumentsResponse,
} from '@/types'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { orderService } from '@/services/admin/orderService'
import { formatDateTime } from '@/utils/formatUtils'

interface OrderLifecycleDocPolicyTabProps {
  orderId?: string | null
  isActive: boolean
  mode: NetworkEnvironment
  refreshToken: number
  onLoadingChange?: (isLoading: boolean) => void
}

export type OrderDocumentPolicyDisplayRow = Readonly<{
  labelKey: string
  fallbackLabel: string
  value: string | number | null
  timestamp?: boolean
  displayVariant?: 'badge' | 'technical'
  fullWidth?: boolean
  retentionProgress?: RetentionDayProgressDisplay
}>

type OrderDocumentRetentionProgressSource = Readonly<
  Pick<
    OrderDocumentSummary,
    'retentionBasisAt' | 'retainUntil' | 'accessEvaluatedAt'
  >
>

type OrderDocumentRetentionProgressSources = Readonly<{
  invoice?: OrderDocumentRetentionProgressSource
  receipt?: OrderDocumentRetentionProgressSource
}>

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000

export type RetentionDayProgressDisplay = Readonly<{
  completedDays: number | null
  totalDays: number
  retentionBasisAt: string | null
  retainUntil: string | null
}>

export const buildRetentionDayProgress = (
  totalDays: number | null,
  source?: OrderDocumentRetentionProgressSource
): RetentionDayProgressDisplay | null => {
  if (totalDays === null || !Number.isInteger(totalDays) || totalDays < 0) {
    return null
  }

  const basisAt = source?.retentionBasisAt ?? null
  const evaluatedAt = source?.accessEvaluatedAt ?? null
  const parsedBasisAt = basisAt ? Date.parse(basisAt) : Number.NaN
  const parsedEvaluatedAt = evaluatedAt ? Date.parse(evaluatedAt) : Number.NaN
  const completedDays =
    Number.isFinite(parsedBasisAt) &&
    Number.isFinite(parsedEvaluatedAt) &&
    parsedEvaluatedAt >= parsedBasisAt
      ? Math.min(
          totalDays,
          Math.floor((parsedEvaluatedAt - parsedBasisAt) / MILLISECONDS_PER_DAY)
        )
      : null

  return {
    completedDays,
    totalDays,
    retentionBasisAt: basisAt,
    retainUntil: source?.retainUntil ?? null,
  }
}

export const formatRetentionDayProgress = (
  totalDays: number | null,
  source?: OrderDocumentRetentionProgressSource
): string | null => {
  const progress = buildRetentionDayProgress(totalDays, source)
  if (!progress) {
    return null
  }
  return `${progress.completedDays ?? '—'}/${progress.totalDays}`
}

export const buildOrderRetentionSnapshotDisplayRows = (
  snapshot: OrderDocumentRetentionSnapshotSummary,
  progressSources?: OrderDocumentRetentionProgressSources
): OrderDocumentPolicyDisplayRow[] => {
  const invoiceProgress = buildRetentionDayProgress(
    snapshot.invoiceRetentionDays,
    progressSources?.invoice
  )
  const receiptProgress = buildRetentionDayProgress(
    snapshot.receiptRetentionDays,
    progressSources?.receipt
  )
  const accessEvaluatedAt =
    progressSources?.invoice?.accessEvaluatedAt ??
    progressSources?.receipt?.accessEvaluatedAt ??
    null

  return [
    {
      labelKey: 'orders.lifecycle.documents.snapshotStatus',
      fallbackLabel: 'Snapshot Status',
      value: snapshot.snapshotStatus,
      displayVariant: 'badge',
    },
    {
      labelKey: 'orders.lifecycle.documents.networkMode',
      fallbackLabel: 'Network Mode',
      value: snapshot.networkMode,
      displayVariant: 'badge',
    },
    {
      labelKey: 'orders.lifecycle.documents.policyVersion',
      fallbackLabel: 'Policy Version',
      value: snapshot.policyVersion,
      displayVariant: 'technical',
    },
    {
      labelKey: 'orders.lifecycle.documents.policyState',
      fallbackLabel: 'Policy State',
      value: snapshot.policyState,
      displayVariant: 'badge',
    },
    {
      labelKey: 'orders.lifecycle.documents.orderCreatedAt',
      fallbackLabel: 'Order Created At',
      value: snapshot.orderCreatedAt,
      timestamp: true,
    },
    {
      labelKey: 'orders.lifecycle.documents.sourceObservedAt',
      fallbackLabel: 'Source Observed At',
      value: snapshot.observedAt,
      timestamp: true,
    },
    {
      labelKey: 'orders.lifecycle.documents.invoiceRetentionDays',
      fallbackLabel: 'Invoice Retention Days',
      value: formatRetentionDayProgress(
        snapshot.invoiceRetentionDays,
        progressSources?.invoice
      ),
      retentionProgress: invoiceProgress ?? undefined,
    },
    {
      labelKey: 'orders.lifecycle.documents.receiptRetentionDays',
      fallbackLabel: 'Receipt Retention Days',
      value: formatRetentionDayProgress(
        snapshot.receiptRetentionDays,
        progressSources?.receipt
      ),
      retentionProgress: receiptProgress ?? undefined,
    },
    {
      labelKey: 'orders.lifecycle.documents.conflictReason',
      fallbackLabel: 'Conflict Reason',
      value: snapshot.conflictReason,
      fullWidth: true,
    },
    {
      labelKey: 'orders.lifecycle.documents.accessEvaluatedAt',
      fallbackLabel: 'Access Evaluated At',
      value: accessEvaluatedAt,
      timestamp: true,
      fullWidth: true,
    },
  ]
}

const getBadgeColorScheme = (value: string): string => {
  switch (value) {
    case 'valid':
    case 'resolved':
      return 'green'
    case 'invalid':
      return 'red'
    case 'missing':
    case 'unmapped_plan_hold':
    case 'policy_conflict_hold':
      return 'orange'
    case 'mainnet':
      return 'blue'
    case 'testnet':
      return 'purple'
    default:
      return 'gray'
  }
}

export function OrderLifecycleDocPolicyTab({
  orderId,
  isActive,
  mode,
  refreshToken,
  onLoadingChange,
}: OrderLifecycleDocPolicyTabProps) {
  const { t } = useTranslation()
  const locale = typeof window !== 'undefined' ? navigator.language : 'en-US'
  const [policyData, setPolicyData] = useState<OrderDocumentsResponse | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [hasAttempted, setHasAttempted] = useState(false)
  const requestSequence = useRef(0)
  const lastRefreshToken = useRef(refreshToken)

  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  useEffect(
    () => () => {
      onLoadingChange?.(false)
    },
    [onLoadingChange]
  )

  useEffect(() => {
    requestSequence.current += 1
    setPolicyData(null)
    setIsLoading(false)
    setErrorMessage(null)
    setHasAttempted(false)
  }, [mode, orderId])

  useEffect(
    () => () => {
      requestSequence.current += 1
    },
    []
  )

  const refreshPolicy = useCallback(async () => {
    if (!orderId) {
      return
    }

    const requestId = ++requestSequence.current
    setIsLoading(true)
    setErrorMessage(null)

    try {
      const response = await orderService.getOrderDocuments(orderId, mode)
      if (requestId !== requestSequence.current) {
        return
      }
      setPolicyData(response)
    } catch (error) {
      if (requestId !== requestSequence.current) {
        return
      }
      setErrorMessage(
        error instanceof Error
          ? error.message
          : t(
              'orders.lifecycle.docPolicy.loadFailed',
              'Failed to load document policy.'
            )
      )
    } finally {
      if (requestId === requestSequence.current) {
        setIsLoading(false)
      }
    }
  }, [mode, orderId, t])

  useEffect(() => {
    if (!isActive || !orderId || hasAttempted) {
      return
    }
    setHasAttempted(true)
    void refreshPolicy()
  }, [hasAttempted, isActive, orderId, refreshPolicy])

  useEffect(() => {
    if (lastRefreshToken.current === refreshToken) {
      return
    }
    if (!isActive || !orderId) {
      lastRefreshToken.current = refreshToken
      return
    }
    if (isLoading) {
      return
    }
    lastRefreshToken.current = refreshToken
    setHasAttempted(true)
    void refreshPolicy()
  }, [isActive, isLoading, orderId, refreshPolicy, refreshToken])

  const snapshot = policyData?.retentionSnapshot ?? null

  const renderInfoRow = (row: OrderDocumentPolicyDisplayRow) => {
    const formattedValue =
      row.timestamp && typeof row.value === 'string'
        ? formatDateTime(row.value, { locale })
        : row.value

    const baseRenderedValue =
      formattedValue === null || formattedValue === '' ? (
        <Text fontSize="sm" color="gray.500">
          —
        </Text>
      ) : row.displayVariant === 'badge' &&
        typeof formattedValue === 'string' ? (
        <Badge
          colorScheme={getBadgeColorScheme(formattedValue)}
          textTransform="none"
          width="fit-content"
        >
          {formattedValue}
        </Badge>
      ) : (
        <Text
          fontSize="sm"
          fontFamily={row.displayVariant === 'technical' ? 'mono' : undefined}
          wordBreak="break-word"
        >
          {formattedValue}
        </Text>
      )

    const renderedValue = row.retentionProgress ? (
      <VStack align="stretch" spacing={1.5}>
        {baseRenderedValue}
        <Progress
          value={
            row.retentionProgress.completedDays === null
              ? 0
              : row.retentionProgress.totalDays === 0
                ? 100
                : (row.retentionProgress.completedDays /
                    row.retentionProgress.totalDays) *
                  100
          }
          size="xs"
          colorScheme="blue"
          borderRadius="full"
          opacity={row.retentionProgress.completedDays === null ? 0.45 : 1}
          aria-label={`${t(row.labelKey, row.fallbackLabel)}: ${row.value ?? '—'}`}
        />
        <Text fontSize="xs" color="gray.500" wordBreak="break-word">
          {row.retentionProgress.retentionBasisAt
            ? formatDateTime(row.retentionProgress.retentionBasisAt, { locale })
            : '—'}
          {' → '}
          {row.retentionProgress.retainUntil
            ? formatDateTime(row.retentionProgress.retainUntil, { locale })
            : '—'}
        </Text>
      </VStack>
    ) : (
      baseRenderedValue
    )

    return (
      <Box
        key={row.labelKey}
        gridColumn={row.fullWidth ? { base: 'auto', md: '1 / -1' } : undefined}
      >
        <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={1}>
          {t(row.labelKey, row.fallbackLabel)}
        </Text>
        {renderedValue}
      </Box>
    )
  }

  if (!orderId) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Text fontSize="sm">
          {t('orders.lifecycle.docPolicy.noOrder', 'Order ID is missing.')}
        </Text>
      </Alert>
    )
  }

  return (
    <VStack align="stretch" spacing={4}>
      {errorMessage && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">{errorMessage}</Text>
        </Alert>
      )}

      {snapshot && (
        <Box borderWidth="1px" borderRadius="md" p={4}>
          <VStack align="stretch" spacing={4}>
            <Text fontWeight="semibold">
              {t(
                'orders.lifecycle.documents.retentionSnapshotTitle',
                'Retention Policy Snapshot'
              )}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={8} spacingY={4}>
              {buildOrderRetentionSnapshotDisplayRows(
                snapshot,
                policyData?.documents
              ).map(renderInfoRow)}
            </SimpleGrid>
          </VStack>
        </Box>
      )}
    </VStack>
  )
}
